type HealthConditionDisclaimer = {
  [k: string]: {
    anchorText: string;
    descriptionMarkup: string;
  };
};

type HealthCondition = {
  health_id: string;
  subdisorder_name: string;
  is_linkage_test?: boolean;
  is_linkage_test_for_v5?: string;
  is_ancestry_based_risk?: boolean;
};

export const conditionDisclaimers: HealthConditionDisclaimer = {
  ancestryRisk: {
    anchorText: 'This is an ancestry risk*',
    descriptionMarkup: `
        <h3 class="h6">* What is an ancestry risk?</h3>
        <p>
            Ancestry-based risk refers to the increased likelihood of certain breeds developing a disease or condition based on their genetic ancestry, plus other genetic factors like sex and markers for the condition. These risk scores can be used, in tandem with diagnostics tools, to help identify and manage potential health issues in relevant breeds. Ancestry-based risk models are a novel approach for predicting disease, powered by Embark Research and survey data shared by our customers.
        </p>
    `,
  },
  linkageTest: {
    anchorText: 'This is a Linkage Test*',
    descriptionMarkup: `
      <h3 class="disclaimerDescriptionText--header h6">* What is a linkage test?</h3>
      <p>DNA sequences that are close together on a chromosome tend to be inherited together. Because of this, we can use genetic variation surrounding a specific variant (i.e. "linked" to it) to infer the presence or absence of a variant that is linked to a health condition or trait.</p>
      <p>Linkage tests are not as predictive of your dog's true genotype as direct assays, that we use on most other genetic conditions we test for.</p>
    `,
  },
  sod1a: {
    anchorText: 'This is the SOD1A Variant*',
    descriptionMarkup: `
      <h3 class="disclaimerDescriptionText--header h6">* This is the SOD1A variant?</h3>
      <p>Please note: While we test for the SOD1A variant, we do not test for the SOD1B variant at this time. Degenerative Myelopathy genotype results apply only to SOD1A.</p>
    `,
  },
};

export function getDisclaimerTypeByCondition(
  healthCondition: HealthCondition
): string {
  if (
    healthCondition.hasOwnProperty('is_ancestry_based_risk') &&
    healthCondition.is_ancestry_based_risk
  ) {
    return 'ancestryRisk';
  }

  if (
    (healthCondition.hasOwnProperty('is_linkage_test') &&
      healthCondition.is_linkage_test) ||
    (healthCondition.hasOwnProperty('is_linkage_test_for_v5') &&
      healthCondition.is_linkage_test_for_v5 === 'TRUE')
  ) {
    return 'linkageTest';
  }
  if (
    (healthCondition.hasOwnProperty('health_id') &&
      healthCondition.health_id === '090400') ||
    (healthCondition.hasOwnProperty('subdisorder_name') &&
      healthCondition.subdisorder_name === '(SOD1A)')
  ) {
    return 'sod1a';
  }

  return '';
}

export function getDisclaimerAnchor(
  disclaimerType: string,
  anchorLocation: string,
  suffix: string | undefined
): string {
  if (!disclaimerType || !anchorLocation) {
    return '';
  }
  let anchorIDString = `disclaimer-${disclaimerType}-${anchorLocation}`;
  if (typeof suffix === 'string') {
    anchorIDString = `${anchorIDString}-${suffix}`;
  }
  return anchorIDString;
}

export function getDisclaimerAnchorLinkText(
  anchorID: string,
  anchorText: string
): string {
  if (!anchorID || !anchorText) {
    return '';
  }
  return `<a href="#${anchorID}" class="disclaimerLink">${anchorText}</a>`;
}

export function getDisclaimerDescriptionMarkup(
  anchorID: string,
  desciptionMarkup: string
): string {
  if (!anchorID || !desciptionMarkup) {
    return '';
  }
  return `
  <div id="${anchorID}" class="disclaimerDescriptionText">
    ${desciptionMarkup}
  </div>`;
}
